/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  OutlinedInput,
} from '@mui/material';

import { SERVICE_TYPES, ServiceType } from 'shared';

import { HUModal2, HUModal2Props } from '../../../../components/HUModal2/HUModal2';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../../i18n';
import { SUPlanApiService } from '../../../../services/SUPlanApiService';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { PlanComputeCreateRequest } from '../../../../types/su-plan/requests/PlanComputeCreateRequest';
import { PlanComputeCreateResponse } from '../../../../types/su-plan/requests/PlanComputeCreateResponse';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useIsMounted } from '../../../../utils/useIsMounted';
import { useObjectState } from '../../../../utils/useObjectState';
import { useValidationTranslation } from '../../../../utils/useValidationTranslation';
import {
  CreatePlanComputeDialogValues,
  formInitialValues,
  validationSchema,
} from './CreatePlanComputeDialog.miscellaneous';

export type CreatePlanComputeDialogOnSuccessCallback = (params: { payload: PlanComputeCreateResponse }) => any;

type CreatePlanComputeDialogProps = Pick<HUModal2Props, 'open'> &
  Required<Pick<HUModal2Props, 'onCancel'>> & {
    className?: string;
    onSuccess: CreatePlanComputeDialogOnSuccessCallback;
  };

export function CreatePlanComputeDialog(props: CreatePlanComputeDialogProps) {
  const { className, open, onCancel, onSuccess } = props;
  const formClasses = useFormStyles();

  const { t } = useTranslation([Namespaces.Admin]);
  const { vtr } = useValidationTranslation();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useObjectState<{
    isOpen: boolean;
    error: string;
  }>({ isOpen: false, error: '' });
  const isMounted = useIsMounted();

  const serviceTypes = SERVICE_TYPES;

  const initialValues = useMemo<CreatePlanComputeDialogValues>(
    () => ({
      ...formInitialValues,
    }),
    [],
  );

  const { setApiError, getFieldApiError, resetFieldApiError } = useApiValidation();

  const onFormSubmit = useCallback(
    async (values: CreatePlanComputeDialogValues) => {
      try {
        setIsProcessing(true);
        const data: PlanComputeCreateRequest = {
          ...values,
        };
        const {
          data: { payload },
        } = await SUPlanApiService.createPlanCompute({ data });
        if (!isMounted.current) {
          return;
        }
        onSuccess({ payload });
      } catch (e) {
        if (!isMounted.current) {
          return;
        }
        const error = e as ApiClientError;
        const validation = setApiError(error);
        if (validation.errorType === 'unexpected' || validation.errorType === 'common') {
          setErrorModal({
            isOpen: true,
            error: `manage_plans.dialogs.errors.${validation.error ?? ''}`,
          });
        }
      } finally {
        if (!isMounted.current) {
          // eslint-disable-next-line no-unsafe-finally
          return;
        }
        setIsProcessing(false);
      }
    },
    [isMounted, onSuccess, setApiError, setErrorModal],
  );

  const formikProps = useFormik<CreatePlanComputeDialogValues>({
    validationSchema,
    enableReinitialize: true,
    initialValues,
    onSubmit: onFormSubmit,
  });

  const { values, touched, errors, handleBlur, handleChange, handleSubmit, setFieldValue, submitForm, resetForm } =
    formikProps;

  useEffect(() => {
    if (!open) {
      resetForm({ values: { ...formInitialValues } });
    }
  }, [resetForm, open]);

  const selectedServiceType = useMemo(
    () => serviceTypes.find(type => type === values.serviceType) ?? null,
    [serviceTypes, values.serviceType],
  );

  const getError = (field: keyof CreatePlanComputeDialogValues): string => {
    return errors[field] || getFieldApiError(field) || '';
  };

  const hasError = (field: keyof CreatePlanComputeDialogValues): boolean => {
    return !!touched[field] && !!getError(field);
  };

  const onTextFieldChange = (name: keyof CreatePlanComputeDialogValues, event: React.ChangeEvent) => {
    handleChange(event);
    resetFieldApiError(name);
  };

  const getLabelKey = (key: string): string => {
    return `manage_plans.dialogs.labels.${key}`;
  };

  return (
    <HUModal2
      open={open}
      okButtonText={t('manage_plans.dialogs.buttons.submit')}
      okButtonProps={{ disabled: !formikProps.dirty || isProcessing }}
      cancelButtonText={t('manage_plans.dialogs.buttons.cancel')}
      onCancel={onCancel}
      onClose={onCancel}
      disableRestoreFocus
      onOk={submitForm}
      size="big"
      title={t('manage_plans.dialogs.titles.compute_plan.create')}
    >
      <HUSpin spinning={isProcessing} delay={50}>
        <form noValidate className={cx(className)} onSubmit={handleSubmit}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('name')}
            variant="outlined"
          >
            <InputLabel htmlFor="name">{t(getLabelKey('name'))}</InputLabel>
            <OutlinedInput
              id="name"
              name="name"
              label={t(getLabelKey('name'))}
              error={hasError('name')}
              fullWidth
              type="text"
              value={values.name}
              onChange={onTextFieldChange.bind(null, 'name')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('name') && (
              <FormHelperText>
                {vtr(getError('name'), {
                  field: t(getLabelKey('name')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('cpu')}
            variant="outlined"
          >
            <InputLabel htmlFor="cpu">{t(getLabelKey('cpu'))}</InputLabel>
            <OutlinedInput
              id="cpu"
              name="cpu"
              label={t(getLabelKey('cpu'))}
              error={hasError('cpu')}
              fullWidth
              type="number"
              value={values.cpu}
              onChange={onTextFieldChange.bind(null, 'cpu')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('cpu') && (
              <FormHelperText>
                {vtr(getError('cpu'), {
                  field: t(getLabelKey('cpu')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('memory')}
            variant="outlined"
          >
            <InputLabel htmlFor="memory">{t(getLabelKey('memory'))}</InputLabel>
            <OutlinedInput
              id="memory"
              name="memory"
              label={t(getLabelKey('memory'))}
              error={hasError('memory')}
              fullWidth
              type="number"
              value={values.memory}
              onChange={onTextFieldChange.bind(null, 'memory')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('memory') && (
              <FormHelperText>
                {vtr(getError('memory'), {
                  field: t(getLabelKey('memory')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('replicaMin')}
            variant="outlined"
          >
            <InputLabel htmlFor="replicaMin">{t(getLabelKey('replica_min'))}</InputLabel>
            <OutlinedInput
              id="replicaMin"
              name="replicaMin"
              label={t(getLabelKey('replica_min'))}
              error={hasError('replicaMin')}
              fullWidth
              type="number"
              value={values.replicaMin}
              onChange={onTextFieldChange.bind(null, 'replicaMin')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('replicaMin') && (
              <FormHelperText>
                {vtr(getError('replicaMin'), {
                  field: t(getLabelKey('replica_min')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('replicaMax')}
            variant="outlined"
          >
            <InputLabel htmlFor="replicaMax">{t(getLabelKey('replica_max'))}</InputLabel>
            <OutlinedInput
              id="replicaMax"
              name="replicaMax"
              label={t(getLabelKey('replica_max'))}
              error={hasError('replicaMax')}
              fullWidth
              type="number"
              value={values.replicaMax}
              onChange={onTextFieldChange.bind(null, 'replicaMax')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('replicaMax') && (
              <FormHelperText>
                {vtr(getError('replicaMax'), {
                  field: t(getLabelKey('replica_max')),
                  moreThan: t(getLabelKey('replica_min'))!,
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('replicaDefault')}
            variant="outlined"
          >
            <InputLabel htmlFor="replicaDefault">{t(getLabelKey('replica_default'))}</InputLabel>
            <OutlinedInput
              id="replicaDefault"
              name="replicaDefault"
              label={t(getLabelKey('replica_default'))}
              error={hasError('replicaDefault')}
              fullWidth
              type="number"
              value={values.replicaDefault}
              onChange={onTextFieldChange.bind(null, 'replicaDefault')}
              disabled={false}
              onBlur={handleBlur}
            />
            {hasError('replicaDefault') && (
              <FormHelperText>
                {vtr(getError('replicaDefault'), {
                  field: t(getLabelKey('replica_default')),
                  lessThan: t(getLabelKey('replica_max'))!,
                  moreThan: t(getLabelKey('replica_min'))!,
                })}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('serviceType')}
            variant="outlined"
          >
            <InputLabel htmlFor="serviceType">{t(getLabelKey('service'))}</InputLabel>
            <HUAutocomplete
              popupIcon={<ArrowRoundedIcon />}
              renderInput={renderParams => (
                <OutlinedInput
                  {...destructRenderInputParams(renderParams)}
                  name="serviceType"
                  label={t(getLabelKey('service'))}
                  onBlur={handleBlur}
                />
              )}
              id="serviceType"
              options={serviceTypes}
              disableClearable={true}
              value={selectedServiceType ?? undefined}
              onBlur={handleBlur}
              onChange={(_e: any, value: ServiceType | null) => {
                if (value !== null) {
                  void setFieldValue('serviceType', value, true);
                  resetFieldApiError('serviceType');
                }
              }}
              getOptionLabel={v => t(`general:services.types.${v}.short`)}
            />
            {hasError('serviceType') && (
              <FormHelperText>
                {vtr(getError('serviceType'), {
                  field: t(getLabelKey('service')),
                })}
              </FormHelperText>
            )}
          </FormControl>
          <div className={formClasses.checkboxes}>
            <FormControlLabel
              name="isVisible"
              checked={values.isVisible}
              onChange={handleChange}
              control={<Checkbox color="primary" />}
              label={t(getLabelKey('is_visible'))}
              labelPlacement="end"
              disabled={false}
            />
          </div>
          <div className={formClasses.hiddenSubmitBtn}>
            <Button
              aria-label={t('buttons.submit')}
              type="submit"
              color="primary"
              variant="contained"
              size="medium"
              disabled={!formikProps.dirty || isProcessing}
            >
              &nbsp;
            </Button>
          </div>
        </form>
        <SimpleErrorModal
          open={errorModal.isOpen}
          message={errorModal.error}
          t={t}
          onOk={() => setErrorModal({ isOpen: false })}
        />
      </HUSpin>
    </HUModal2>
  );
}
